import { Group, Text } from '@mantine/core';
import { MoneyText } from './MoneyText';

/** Props for {@link RevenueSplit}. */
export interface RevenueSplitProps {
  /** Cash from cards sold outright, in cents (`sale_revenue_cents`). */
  saleCents: number;
  /** Trade value of cards traded away, in cents (`trade_revenue_cents`). */
  tradeCents: number;
  /** Font size in px. Default `11`. */
  size?: number;
  /** Right-align the parts (for a numeric table column). Default `false`. */
  alignEnd?: boolean;
  /** `data-testid` on the wrapper, for asserting both parts at once. */
  'data-testid'?: string;
}

/**
 * RevenueSplit - "Sales $X . Traded away $Y" under or beside a revenue figure
 * (vendor#1453). The two parts add up to the revenue total exactly; this never
 * replaces the total.
 *
 * Shell: any staff shell. Never (kiosk) - revenue is staff-only data.
 *
 * Both parts are money IN (`--money-in`, mint), except a zero part, which renders flat: trade value is value that left
 * the inventory, the same role the show screens give it (#1446/#1447). Phrasing
 * content only, so it is safe as a StatTile `sub`.
 */
export function RevenueSplit({
  saleCents,
  tradeCents,
  size = 11,
  alignEnd = false,
  'data-testid': testId,
}: RevenueSplitProps) {
  return (
    <Group
      component="span"
      gap={8}
      wrap="wrap"
      justify={alignEnd ? 'flex-end' : undefined}
      style={{ rowGap: 0 }}
      data-testid={testId}
    >
      <Text component="span" fz={size} c="dimmed">
        Sales{' '}
        <MoneyText
          cents={saleCents}
          role={saleCents === 0 ? 'flat' : 'in'}
          size={size}
          weight={600}
        />
      </Text>
      <Text component="span" fz={size} c="dimmed">
        Traded away{' '}
        <MoneyText
          cents={tradeCents}
          role={tradeCents === 0 ? 'flat' : 'in'}
          size={size}
          weight={600}
        />
      </Text>
    </Group>
  );
}
